import { useEffect, useRef } from "react";

/**
 * Soft white spotlight that tracks the mouse cursor.
 * - Subtle radial glow around the pointer
 * - Brightens slightly while moving, fades when idle
 * - Hidden on touch devices (no cursor)
 * - Pointer-events: none, never blocks interaction
 */
const RADIUS = 220;
const ACTIVE_OPACITY = 0.16;
const IDLE_OPACITY = 0.0;
const IDLE_DELAY_MS = 350;
const LERP_POS = 0.22; // higher = snappier tracking
const LERP_OP = 0.08;  // lower = smoother fade

const CursorSpotlight = () => {
  const elRef = useRef(null);
  const stateRef = useRef({
    targetX: -9999,
    targetY: -9999,
    x: -9999,
    y: -9999,
    targetOpacity: 0,
    opacity: 0,
    lastMove: 0,
    rafId: null,
  });

  useEffect(() => {
    if (typeof window === "undefined") return;
    // Skip on touch-primary devices (no hover/pointer)
    if (window.matchMedia("(hover: none), (pointer: coarse)").matches) return;

    const st = stateRef.current;

    const paint = () => {
      // Ease position
      st.x = st.x + (st.targetX - st.x) * LERP_POS;
      st.y = st.y + (st.targetY - st.y) * LERP_POS;
      // Decay opacity after idle
      if (performance.now() - st.lastMove > IDLE_DELAY_MS) {
        st.targetOpacity = IDLE_OPACITY;
      }
      st.opacity = st.opacity + (st.targetOpacity - st.opacity) * LERP_OP;

      if (elRef.current) {
        elRef.current.style.transform = `translate3d(${st.x - RADIUS}px, ${st.y - RADIUS}px, 0)`;
        elRef.current.style.opacity = st.opacity.toFixed(3);
      }
      st.rafId = requestAnimationFrame(paint);
    };
    st.rafId = requestAnimationFrame(paint);

    const onMove = (e) => {
      st.targetX = e.clientX;
      st.targetY = e.clientY;
      st.targetOpacity = ACTIVE_OPACITY;
      st.lastMove = performance.now();
    };
    const onLeave = () => {
      st.targetOpacity = IDLE_OPACITY;
    };

    window.addEventListener("mousemove", onMove, { passive: true });
    window.addEventListener("mouseleave", onLeave);
    document.addEventListener("mouseleave", onLeave);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseleave", onLeave);
      document.removeEventListener("mouseleave", onLeave);
      if (st.rafId) cancelAnimationFrame(st.rafId);
    };
  }, []);

  return (
    <div
      ref={elRef}
      aria-hidden="true"
      data-testid="cursor-spotlight"
      className="pointer-events-none fixed top-0 left-0 z-[100]"
      style={{
        width: `${RADIUS * 2}px`,
        height: `${RADIUS * 2}px`,
        borderRadius: "50%",
        background:
          "radial-gradient(circle, rgba(255,255,255,0.85) 0%, rgba(255,255,255,0.35) 22%, rgba(255,255,255,0.08) 55%, transparent 75%)",
        filter: "blur(20px)",
        mixBlendMode: "screen",
        opacity: 0,
        transform: "translate3d(-9999px, -9999px, 0)",
        willChange: "transform, opacity",
      }}
    />
  );
};

export default CursorSpotlight;
